import React, { useMemo } from 'react';
import { IconUserCircle } from '@tabler/icons-react';
import useUserProfile from '../../hooks/useUserProfile';
import { useSelector } from 'react-redux';
import { getDisplayName, getResolvedPhotoURL } from '../../utils/userDisplay.js';
import './AvatarById.css';

/**
 * AvatarById - A reusable avatar component that displays user avatar by user ID
 * @param {string} userId - The user ID to fetch and display avatar for
 * @param {string} fallbackUsername - Username to display if profile fetch fails
 * @param {string} size - Avatar size: 'small' (32px), 'medium' (48px), 'large' (64px)
 * @param {string} className - Additional CSS classes
 * @param {function} onClick - Click handler for the avatar
 * @param {boolean} showOnlineIndicator - Whether to show online status indicator
 */
const AvatarById = ({
  userId,
  userData = null,
  fallbackUsername = 'User',
  size = 'medium',
  className = '',
  onClick,
  showOnlineIndicator = false
}) => {
  const shouldFetch = !userData;
  const { profile, loading } = useUserProfile(userId, { enabled: shouldFetch });
  const currentUser = useSelector((s) => s.auth.currentUser);

  const sizeClasses = {
    small: 'avatar--small',
    medium: 'avatar--medium',
    large: 'avatar--large'
  };

  // Resolve display avatar similar to PostHeader logic
  const displayAvatar = useMemo(() => {
    if (loading && !userData) return null;
    const user = userData || profile;
    const authFallback = currentUser && userId && currentUser.uid === userId ? currentUser : null;
    const url = getResolvedPhotoURL({ profile: user, authUser: authFallback });
    if (url && url !== 'null' && url !== 'undefined' && String(url).trim() !== '') return url;
    return null;
  }, [profile, loading, currentUser, userId, userData]);

  // Get display name
  const displayName = useMemo(() => {
    if (loading) return fallbackUsername;
    return getDisplayName({ profile, candidates: [fallbackUsername] }) || fallbackUsername;
  }, [profile, loading, fallbackUsername]);

  const avatarClassName = `avatar-by-id ${sizeClasses[size]} ${className}`.trim();

  return (
    <div className={avatarClassName} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      {displayAvatar ? (
        <img
          src={displayAvatar}
          alt={displayName}
          className="avatar-image"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
      ) : null}
      <IconUserCircle
        size={size === 'small' ? 32 : size === 'medium' ? 48 : 64}
        className="avatar-fallback"
        style={{
          display: displayAvatar ? 'none' : 'block',
          color: '#666'
        }}
      />
      {showOnlineIndicator && (
        <div className="avatar-online-indicator" />
      )}
    </div>
  );
};

export default AvatarById;
